/**
 * useAdminLayoutRoleAssignments — query GET /api/admin/menu/layouts/:id/role-assignments (ADR-0034).
 *
 * Backend zwraca `{roles: AdminRole[]}` gdzie AdminRole = 'super_admin' | 'support' |
 * 'finance' | 'readonly' (4-value ENUM z `admin_users.role`). W przeciwieństwie do
 * tenant role templates (UUID IDs), admin używa string enum jako primary identifier.
 *
 * Shell oczekuje shape `{roleIds: string[]}` (`AssignmentsQueryLike`) — adaptujemy
 * w hook, mapując `roles` → `roleIds` (string array, AdminRole IS string).
 */

import type { AdminRole } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export const ADMIN_LAYOUT_ROLE_ASSIGNMENTS_KEY = (layoutId: string | null) =>
  ['admin', 'menu', 'layouts', layoutId, 'role-assignments'] as const;

interface AdminLayoutRoleAssignmentsApiResponse {
  roles: AdminRole[];
}

export interface AdminLayoutRoleAssignmentsResponse {
  roleIds: string[];
}

export function useAdminLayoutRoleAssignments(layoutId: string | null) {
  return useQuery<AdminLayoutRoleAssignmentsResponse>({
    queryKey: ADMIN_LAYOUT_ROLE_ASSIGNMENTS_KEY(layoutId),
    queryFn: async () => {
      const res = await api.get<AdminLayoutRoleAssignmentsApiResponse>(
        `/admin/menu/layouts/${layoutId}/role-assignments`,
      );
      // Backend zwraca `{roles: AdminRole[]}`, shell oczekuje `{roleIds: string[]}`.
      // AdminRole IS string — bezpieczna konwersja bez utraty informacji.
      return { roleIds: res.data.roles };
    },
    enabled: layoutId !== null,
    staleTime: 30_000,
  });
}
